import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'

interface Slide {
  id: string
  slide_id: string | null
  name: string
  slide_barcode?: string
  file_path?: string
  file_size?: number
  file_size_mb?: number
  status: 'pending' | 'processing' | 'ready' | 'failed'
  created_at: string
  case?: number
  case_id?: number
  case_number?: string
  patient_id?: number
  patient_name?: string
  patient_code?: string
}

interface Patient {
  id: string
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
}

const TechnicianWorklist: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [selectedSlide, setSelectedSlide] = useState<Slide | null>(null)
  const [selectedPatient, setSelectedPatient] = useState<string>('')
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showReassignConfirm, setShowReassignConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchSlides()
    fetchPatients()
  }, [])

  const fetchSlides = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/imaging/slides/?page_size=1000')
      if (response.ok) {
        const data = await response.json()
        const allSlides = data.results || data
        // Load all slides (both assigned and unassigned)
        setSlides(allSlides)
      }
    } catch (error) {
      console.error('Error fetching slides:', error)
      toast.error('Không thể tải danh sách ảnh')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPatients = async () => {
    try {
      const response = await fetch('/api/v1/pathology/patients/?page_size=1000')
      if (response.ok) {
        const data = await response.json()
        setPatients(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching patients:', error)
    }
  }

  const openAssignModal = (slide: Slide, isReassign: boolean = false) => {
    setSelectedSlide(slide)
    setSelectedPatient('')

    if (isReassign && slide.patient_name) {
      // Show confirmation for reassignment
      setShowReassignConfirm(true)
    } else {
      setShowAssignModal(true)
    }
  }

  const confirmReassign = () => {
    setShowReassignConfirm(false)
    setShowAssignModal(true)
  }

  const handleAssign = async () => {
    if (!selectedSlide || !selectedPatient) {
      toast.error('Vui lòng chọn bệnh nhân')
      return
    }

    try {
      setIsLoading(true)
      const response = await fetch(`/api/v1/imaging/slides/${selectedSlide.id}/assign-patient/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_id: selectedPatient
        })
      })

      if (response.ok) {
        const result = await response.json()
        toast.success(result.message || 'Đã gán ảnh cho bệnh nhân')
        setShowAssignModal(false)
        fetchSlides()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Không thể gán ảnh')
      }
    } catch (error) {
      console.error('Error assigning slide:', error)
      toast.error('Lỗi khi gán ảnh')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredPatients = patients.filter(p =>
    searchTerm === '' ||
    p.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN')
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: 'Chờ xử lý', color: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'Đang xử lý', color: 'bg-blue-100 text-blue-800' },
      ready: { label: 'Sẵn sàng', color: 'bg-green-100 text-green-800' },
      failed: { label: 'Lỗi', color: 'bg-red-100 text-red-800' }
    }
    const info = statusMap[status as keyof typeof statusMap] || statusMap.pending
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${info.color}`}>
        {info.label}
      </span>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🔬 Worklist - Kỹ Thuật Viên</h1>
        <p className="text-gray-600 mt-1">Quản lý gán ảnh slide cho bệnh nhân</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-3xl font-bold text-red-700">
            {slides.filter(s => !s.case).length}
          </div>
          <div className="text-sm text-red-600 mt-1">⚠️ Chưa gán bệnh nhân</div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-3xl font-bold text-green-700">
            {slides.filter(s => s.case).length}
          </div>
          <div className="text-sm text-green-600 mt-1">✅ Đã gán bệnh nhân</div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-3xl font-bold text-blue-700">
            {slides.filter(s => s.status === 'ready').length}
          </div>
          <div className="text-sm text-blue-600 mt-1">🎯 Ảnh sẵn sàng</div>
        </div>
      </div>

      {/* Slides Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">📋 Danh Sách Ảnh Slide</h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2">Đang tải...</p>
          </div>
        ) : slides.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">Không có ảnh nào</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tên File
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Barcode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thông tin Bệnh nhân
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kích thước
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thời gian
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {slides.map((slide) => (
                  <tr key={slide.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{slide.name}</div>
                      <div className="text-xs text-gray-500">{slide.slide_id || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {slide.slide_barcode || <span className="text-gray-400">Chưa có</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {slide.patient_name ? (
                        <button
                          onClick={() => openAssignModal(slide, true)}
                          className="text-left hover:bg-blue-50 rounded p-2 transition-colors group"
                          title="Click để gán lại bệnh nhân"
                        >
                          <div className="text-sm font-medium text-blue-700 group-hover:text-blue-900">
                            {slide.patient_name}
                          </div>
                          <div className="text-xs text-blue-600 group-hover:text-blue-800">
                            Mã BN: {slide.patient_code}
                          </div>
                          <div className="text-xs text-gray-500">
                            Case: {slide.case_number || slide.case_id}
                          </div>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Chưa gán</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {slide.file_size_mb ? `${slide.file_size_mb.toFixed(2)} MB` : formatFileSize(slide.file_size || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(slide.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(slide.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!slide.case ? (
                        <button
                          onClick={() => openAssignModal(slide, false)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          🔗 Gán bệnh nhân
                        </button>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">✓ Đã gán</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showAssignModal && selectedSlide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                🔗 Gán Ảnh Cho Bệnh Nhân
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Ảnh: <span className="font-medium">{selectedSlide.name}</span>
              </p>
            </div>

            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔍 Tìm kiếm bệnh nhân
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nhập tên hoặc mã bệnh nhân..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="mt-4 max-h-96 overflow-y-auto border border-gray-200 rounded">
                {filteredPatients.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Không tìm thấy bệnh nhân
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredPatients.map((patient) => (
                      <div
                        key={patient.id}
                        onClick={() => setSelectedPatient(patient.id)}
                        className={`flex items-center p-4 cursor-pointer transition-all border-l-4 ${
                          selectedPatient === patient.id
                            ? 'bg-green-50 border-green-500'
                            : 'border-transparent hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-center w-6 h-6 mr-3 flex-shrink-0">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            selectedPatient === patient.id
                              ? 'border-green-600 bg-green-600'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {selectedPatient === patient.id && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${
                            selectedPatient === patient.id ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            {patient.full_name}
                          </div>
                          <div className={`text-xs mt-1 ${
                            selectedPatient === patient.id ? 'text-green-700' : 'text-gray-500'
                          }`}>
                            Mã BN: {patient.patient_id} • {patient.gender === 'M' ? 'Nam' : 'Nữ'} • NS: {new Date(patient.date_of_birth).toLocaleDateString('vi-VN')}
                          </div>
                        </div>
                        {selectedPatient === patient.id && (
                          <div className="text-green-600 font-bold text-lg ml-2">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedPatient || isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Đang xử lý...' : '✅ Gán'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Confirmation Modal */}
      {showReassignConfirm && selectedSlide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-900">
                ⚠️ Xác nhận gán lại bệnh nhân
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-700 mb-4">
                Ảnh <span className="font-medium">{selectedSlide.name}</span> đã được gán cho:
              </p>
              <div className="bg-blue-50 p-3 rounded border border-blue-200 mb-4">
                <div className="text-sm font-medium text-blue-900">
                  {selectedSlide.patient_name}
                </div>
                <div className="text-xs text-blue-700">
                  Mã BN: {selectedSlide.patient_code}
                </div>
              </div>
              <p className="text-sm text-red-600 font-medium">
                Bạn có chắc muốn gán lại ảnh này cho bệnh nhân khác?
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Hành động này sẽ thay đổi thông tin case hiện tại.
              </p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReassignConfirm(false)
                  setSelectedSlide(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={confirmReassign}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                ✓ Xác nhận gán lại
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TechnicianWorklist
